import React, { useRef, useState } from "react";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { IconTrash } from "@velaro/velaro-component-lib";
import IconTrash1 from "@/icons/IconTrash1";

interface Props {
  setStageLogoFile: (file: File | null) => void;
  update: (updates: Partial<StyleConfigModel>) => void;
  workingStyleConfig: StyleConfigModel;
  field: keyof Pick<StyleConfigModel, "logoUrl" | "queueGraphicUrl" | "headerBackgroundImageUrl" | "launcherIconUrl">;
  label?: string;
}

export default function LogoUpload(props: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      props.update({ [props.field]: e.target?.result?.toString() });
    };
    reader.readAsDataURL(file);
    props.setStageLogoFile(file);
    setFileName(file.name);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleBrowse = () => {
    fileInput.current?.click();
  };

  const handleRemove = () => {
    props.update({ [props.field]: "" });
    props.setStageLogoFile(null);
    setFileName(null);
  };

  const url = props.workingStyleConfig[props.field];

  return (
    <div>
      <div className="text-sm font-medium mb-2">{props.label}</div>

      {url ? (
        <div className="border rounded-md p-4 bg-slate-50 flex items-center justify-between mb-2">
          <div className="flex items-center space-x-4 overflow-hidden">
            <img
              src={url}
              alt={`${props.label} preview`}
              className="h-10 w-10 object-contain"
            />
            <span className="truncate text-sm text-slate-500">
              {fileName ?? `Uploaded ${props.label?.toLowerCase()}`}
            </span>
          </div>
          <button
            onClick={handleRemove}
            className="text-slate-500 hover:text-red-600 transition"
            aria-label="Remove file"
          >
            <IconTrash className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label
          onClick={handleBrowse}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`w-full flex items-center justify-center border-2 border-dashed rounded-md px-4 py-6 text-sm text-slate-500 cursor-pointer transition ${
            dragOver ? "bg-blue-50 border-cornflower-blue-500" : "bg-slate-50"
          }`}
        >
          {/* <UploadCloud className="mr-2" size={18} /> */}
          Drag & Drop your file or{" "}
          <span className="underline ml-1">Browse</span>
        </label>
      )}

      <input
        type="file"
        accept="image/png, image/svg+xml"
        ref={fileInput}
        className="hidden"
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) handleFile(file);
        }}
      />

      <div className="mt-2 text-xs font-normal text-slate-500">
        Up to 5MB • Supports PNG, SVG • 42px × 200px
      </div>
    </div>
  );
}
